import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Fade,
  LinearProgress,
  IconButton,
  Backdrop,
  Chip,
  keyframes,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward,
  ArrowBack,
  SkipNext,
  LightbulbOutlined,
  Keyboard,
} from '@mui/icons-material';
import { useTour } from '../contexts/TourContext';

// Floating animation for the tooltip
const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
`;

export default function TourComponent() {
  const {
    isOpen,
    currentStep,
    steps,
    nextStep,
    prevStep,
    closeTour,
    skipTour,
  } = useTour();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showKeyboardHint, setShowKeyboardHint] = useState(true);

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          e.preventDefault();
          nextStep();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (!isFirstStep) prevStep();
          break;
        case 'Escape':
          e.preventDefault();
          closeTour();
          break;
      }
      // Hide keyboard hint after first interaction
      setShowKeyboardHint(false);
    },
    [isOpen, isFirstStep, nextStep, prevStep, closeTour]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (isOpen && currentStepData?.target) {
      const element = document.querySelector(
        currentStepData.target
      ) as HTMLElement;
      if (element) {
        setTargetElement(element);

        // Calculate position for the tour tooltip
        const rect = element.getBoundingClientRect();
        const placement = currentStepData.placement || 'bottom';
        const tooltipWidth = 320; // maxWidth of tooltip
        const tooltipHeight = 280; // estimated height
        const offset = 20; // spacing from target element

        let top = 0;
        let left = 0;

        switch (placement) {
          case 'top':
            top = rect.top - tooltipHeight - offset;
            left = rect.left + rect.width / 2;
            break;
          case 'bottom':
            top = rect.bottom + offset;
            left = rect.left + rect.width / 2;
            break;
          case 'left':
            top = rect.top + rect.height / 2;
            left = rect.left - tooltipWidth - offset;
            break;
          case 'right':
            top = rect.top + rect.height / 2;
            left = rect.right + offset;
            break;
        }

        // Ensure tooltip stays within viewport bounds
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Adjust horizontal position (account for centering)
        const margin = 20;
        const leftEdge = left - tooltipWidth / 2;
        const rightEdge = left + tooltipWidth / 2;

        if (leftEdge < margin) {
          left = margin + tooltipWidth / 2; // Position so left edge is at margin
        } else if (rightEdge > viewportWidth - margin) {
          left = viewportWidth - margin - tooltipWidth / 2; // Position so right edge is at margin
        }

        // Adjust vertical position
        if (top < margin) {
          top = margin;
        } else if (top + tooltipHeight > viewportHeight - margin) {
          top = viewportHeight - tooltipHeight - margin;
        }

        setPosition({ top, left: left - tooltipWidth / 2 }); // Convert center position to left edge

        // Scroll element into view
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });

        // Highlight the target element with pulsing animation
        element.style.position = 'relative';
        element.style.zIndex = '1400';
        element.style.borderRadius = '8px';
        element.style.outline = '3px solid #1976d2';
        element.style.outlineOffset = '4px';
        element.style.transition = 'all 0.3s ease';
        // Add pulsing class
        element.classList.add('tour-target-pulse');
      }
    }

    return () => {
      // Cleanup highlight
      if (targetElement) {
        targetElement.style.position = '';
        targetElement.style.zIndex = '';
        targetElement.style.borderRadius = '';
        targetElement.style.outline = '';
        targetElement.style.outlineOffset = '';
        targetElement.style.transition = '';
        targetElement.classList.remove('tour-target-pulse');
      }
    };
  }, [isOpen, currentStep, currentStepData, targetElement]);

  // Reset keyboard hint when tour restarts
  useEffect(() => {
    if (isOpen && currentStep === 0) {
      setShowKeyboardHint(true);
    }
  }, [isOpen, currentStep]);

  if (!isOpen || !currentStepData) {
    return null;
  }

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <>
      {/* Global styles for pulsing animation */}
      <style>
        {`
          @keyframes tour-pulse {
            0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.5); }
            70% { box-shadow: 0 0 0 12px rgba(25, 118, 210, 0); }
            100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
          }
          .tour-target-pulse {
            animation: tour-pulse 2s infinite;
          }
        `}
      </style>

      {/* Backdrop */}
      <Backdrop
        open={isOpen}
        sx={{
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
        }}
        onClick={closeTour}
      />

      {/* Tour Tooltip */}
      <Fade in={isOpen} timeout={300}>
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: 360,
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            p: 0,
            zIndex: 1400,
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            animation: `${float} 3s ease-in-out infinite`,
          }}
        >
          {/* Gradient Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
              p: 2,
              pb: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LightbulbOutlined sx={{ color: 'white', fontSize: 20 }} />
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{ fontWeight: 600, color: 'white' }}
              >
                {currentStepData.title}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={closeTour}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 2.5 }}>
            {/* Progress */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
              >
                <Chip
                  label={`Step ${currentStep + 1} of ${steps.length}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 24, fontSize: '0.75rem' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {Math.round(progress)}% complete
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  mt: 1,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    background:
                      'linear-gradient(90deg, #1E23FE 0%, #0D9E6F 100%)',
                  },
                }}
              />
            </Box>

            {/* Content */}
            <Typography
              variant="body2"
              sx={{ mb: 2.5, lineHeight: 1.7, color: 'text.secondary' }}
            >
              {currentStepData.content}
            </Typography>

            {/* Keyboard Hint */}
            {showKeyboardHint && isFirstStep && (
              <Fade in={showKeyboardHint}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'grey.300',
                  }}
                >
                  <Keyboard sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Use arrow keys to navigate, Esc to close
                  </Typography>
                </Box>
              </Fade>
            )}

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                size="small"
                onClick={skipTour}
                startIcon={<SkipNext />}
                sx={{ color: 'text.secondary', textTransform: 'none' }}
              >
                Skip
              </Button>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {!isFirstStep && (
                  <Button
                    size="small"
                    onClick={prevStep}
                    startIcon={<ArrowBack />}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                  >
                    Back
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={nextStep}
                  endIcon={!isLastStep ? <ArrowForward /> : undefined}
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    background:
                      'linear-gradient(90deg, #1E23FE 0%, #0D9E6F 100%)',
                    '&:hover': {
                      background:
                        'linear-gradient(90deg, #1a1fd9 0%, #0b8a61 100%)',
                    },
                  }}
                >
                  {isLastStep ? "Let's Go!" : 'Next'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </>
  );
}
